import React, { useState, useMemo } from 'react';
import { useClientAuthStore } from '@/store/useClientAuthStore';
import { Link, useNavigate } from 'react-router-dom';
import { CheckIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Validación de requisitos de contraseña en tiempo real
  const passwordRequirements = useMemo(() => {
    const password = formData.password;
    return {
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
  }, [formData.password]);

  const register = useClientAuthStore((state) => state.register);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validar requisitos de contraseña
    const allRequirementsMet = Object.values(passwordRequirements).every(req => req);
    if (!allRequirementsMet) {
      setError('La contraseña no cumple con todos los requisitos de seguridad.');
      setLoading(false);
      return;
    }

    try {
      await register(formData);
      navigate('/home');
    } catch (err) {
      const msg = (err as any)?.message || 'Error al crear la cuenta. Verifica los datos.';
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    borderColor: '#ECF0F1',
    color: '#2C3E50',
    backgroundColor: '#FFFFFF'
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#ECF0F1' }}>
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl">
        <div className="text-center">
          {/* Business Logo */}
          <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6 p-2" style={{ backgroundColor: '#FF6B35' }}>
            <img 
              src="/images/centro-automotriz-hlfrd-icon-2.ico" 
              alt="Centro Automotriz HLFRD"
              className="w-12 h-12 object-contain"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </div>
          
          <h2 className="text-3xl font-bold" style={{ color: '#2C3E50' }}>
            Centro Automotriz HLFRD
          </h2>
          <p className="mt-2 text-sm" style={{ color: '#34495E' }}>
            Portal de Clientes - Crear Cuenta
          </p>
        </div>
        
        <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium mb-1" style={{ color: '#34495E' }}>
              Nombre completo *
            </label>
            <input
              id="fullName"
              name="fullName"
              type="text"
              required
              className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200"
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#FF6B35'}
              onBlur={(e) => e.target.style.borderColor = '#ECF0F1'}
              placeholder="Juan Pérez"
              value={formData.fullName}
              onChange={handleChange}
            />
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1" style={{ color: '#34495E' }}>
              Email *
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200"
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#FF6B35'}
              onBlur={(e) => e.target.style.borderColor = '#ECF0F1'}
              placeholder="juan@email.com"
              value={formData.email}
              onChange={handleChange}
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium mb-1" style={{ color: '#34495E' }}>
              Teléfono
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200"
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#FF6B35'}
              onBlur={(e) => e.target.style.borderColor = '#ECF0F1'}
              placeholder="123-456-7890"
              value={formData.phone}
              onChange={handleChange}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-1" style={{ color: '#34495E' }}>
              Contraseña *
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                required
                className="w-full px-4 py-3 pr-12 border-2 rounded-xl focus:outline-none transition-all duration-200"
                style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#FF6B35'}
                onBlur={(e) => e.target.style.borderColor = '#ECF0F1'}
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
            </div>

            {/* Indicadores de requisitos de contraseña */}
            <div className="mt-3 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-3">
                Requisitos de la contraseña:
              </p>
              <div className="space-y-2">
                <div className={`flex items-center space-x-2 text-sm ${
                  passwordRequirements.minLength ? 'text-green-600' : 'text-gray-500'
                }`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    passwordRequirements.minLength
                      ? 'bg-green-100 border-green-500'
                      : 'bg-gray-100 border-gray-300'
                  } border`}>
                    {passwordRequirements.minLength && (
                      <span className="text-green-600 text-xs">✓</span>
                    )}
                  </div>
                  <span>Al menos 8 caracteres</span>
                </div>

                <div className={`flex items-center space-x-2 text-sm ${
                  passwordRequirements.hasUpperCase ? 'text-green-600' : 'text-gray-500'
                }`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    passwordRequirements.hasUpperCase
                      ? 'bg-green-100 border-green-500'
                      : 'bg-gray-100 border-gray-300'
                  } border`}>
                    {passwordRequirements.hasUpperCase && (
                      <span className="text-green-600 text-xs">✓</span>
                    )}
                  </div>
                  <span>Una letra mayúscula</span>
                </div>

                <div className={`flex items-center space-x-2 text-sm ${
                  passwordRequirements.hasLowerCase ? 'text-green-600' : 'text-gray-500'
                }`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    passwordRequirements.hasLowerCase
                      ? 'bg-green-100 border-green-500'
                      : 'bg-gray-100 border-gray-300'
                  } border`}>
                    {passwordRequirements.hasLowerCase && (
                      <span className="text-green-600 text-xs">✓</span>
                    )}
                  </div>
                  <span>Una letra minúscula</span>
                </div>

                <div className={`flex items-center space-x-2 text-sm ${
                  passwordRequirements.hasNumber ? 'text-green-600' : 'text-gray-500'
                }`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    passwordRequirements.hasNumber
                      ? 'bg-green-100 border-green-500'
                      : 'bg-gray-100 border-gray-300'
                  } border`}>
                    {passwordRequirements.hasNumber && (
                      <span className="text-green-600 text-xs">✓</span>
                    )}
                  </div>
                  <span>Un número</span>
                </div>

                <div className={`flex items-center space-x-2 text-sm ${
                  passwordRequirements.hasSpecialChar ? 'text-green-600' : 'text-gray-500'
                }`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    passwordRequirements.hasSpecialChar
                      ? 'bg-green-100 border-green-500'
                      : 'bg-gray-100 border-gray-300'
                  } border`}>
                    {passwordRequirements.hasSpecialChar && (
                      <span className="text-green-600 text-xs">✓</span>
                    )}
                  </div>
                  <span>Un carácter especial (!@#$%^&*)</span>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
              <div className="text-red-700 text-sm">{error}</div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 text-white font-semibold rounded-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-4 disabled:opacity-50 disabled:transform-none"
              style={{ 
                backgroundColor: loading ? '#FF8C69' : '#FF6B35',
                boxShadow: '0 4px 15px rgba(255, 107, 53, 0.3)'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#FF8C69')}
              onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = '#FF6B35')}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creando cuenta...
                </div>
              ) : 'Crear Cuenta'}
            </button>
          </div>

          <div className="text-center pt-4">
            <p className="text-sm" style={{ color: '#34495E' }}>
              ¿Ya tienes cuenta?{' '}
              <Link 
                to="/login" 
                className="font-medium hover:underline transition-colors duration-200"
                style={{ color: '#FF6B35' }}
                onMouseEnter={(e) => e.target.style.color = '#FF8C69'}
                onMouseLeave={(e) => e.target.style.color = '#FF6B35'}
              >
                Inicia sesión aquí
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;